import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Button, Col, Row } from 'antd';
import { Box } from 'atoms';
import { AntdTable } from 'components/custom-table/Style';
import InfoBanner from 'componentsv2/common/InfoBanner';
import { numberWithCommas, rateWithCommas } from 'utils/NumberCommaSeprator';
import { formatIstDateTime, MODEL_COST_PLUS } from '../warehousing.constants';
import FixedCostsPanel from './FixedCostsPanel';
import { formatChargeBasis, formatEditLogValue } from './editLogValue';
import {
	EmptyState,
	NumCell,
	Panel,
	PanelHeader,
	PanelSubtitle,
	PanelTitle,
} from './warehousingStyles';

const money = (value) =>
	value === null || value === undefined ? '—' : `₹ ${numberWithCommas(Math.round(value))}`;

const rate = (value) =>
	value === null || value === undefined ? '—' : `₹ ${rateWithCommas(value)}`;

const chargeColumns = [
	{ title: 'Charge Head', dataIndex: 'charge_head', key: 'charge_head' },
	{
		title: 'Basis',
		dataIndex: 'basis',
		key: 'basis',
		render: (value) => formatChargeBasis(value),
	},
	{
		title: 'Rate',
		dataIndex: 'rate',
		key: 'rate',
		align: 'right',
		// A rate is not money: it keeps up to 4dp, so a Cross Dock rate of 17.5555/bag
		// reads as configured rather than rounded to the rupee.
		render: (value) => <NumCell>{rate(value)}</NumCell>,
	},
];

const editLogColumns = [
	{ title: 'Field Edited', dataIndex: 'field', key: 'field' },
	{
		title: 'Previous Value',
		dataIndex: 'previous_value',
		key: 'previous_value',
		render: (value) => formatEditLogValue(value),
	},
	{
		title: 'Updated Value',
		dataIndex: 'updated_value',
		key: 'updated_value',
		render: (value) => formatEditLogValue(value),
	},
	{
		title: 'Updated At',
		dataIndex: 'updated_at',
		key: 'updated_at',
		render: (value) => formatIstDateTime(value),
	},
	{ title: 'Updated By', dataIndex: 'updated_by', key: 'updated_by', render: (v) => v || '—' },
	{ title: 'Reason', dataIndex: 'reason', key: 'reason', render: (v) => v || '—' },
];

const slabColumns = [
	{
		title: 'Slab',
		key: 'slab',
		render: (_, __, index) => `Slab ${index + 1}`,
	},
	{
		title: 'Min',
		dataIndex: 'slab_min',
		key: 'slab_min',
		align: 'right',
		render: (value) => <NumCell>{numberWithCommas(value ?? 0)}</NumCell>,
	},
	{
		title: 'Max',
		dataIndex: 'slab_max',
		key: 'slab_max',
		align: 'right',
		render: (value) => <NumCell>{numberWithCommas(value ?? 0)}</NumCell>,
	},
	{
		title: 'Rate / Order',
		dataIndex: 'rate_per_order',
		key: 'rate_per_order',
		align: 'right',
		render: (value) => <NumCell>{rate(value)}</NumCell>,
	},
];

/** Fixed tab — the project's revenue model (read-only) beside its fixed cost heads. */
export default function FixedTab({
	projectId,
	revenueModel,
	fixedCosts,
	dashboard,
	loading,
	onDataChanged,
}) {
	const navigate = useNavigate();

	const additionalCharges = dashboard?.additional_charges || [];
	const additionalChargesTotal = additionalCharges.reduce(
		(sum, charge) => sum + (Number(charge.amount) || 0),
		0
	);
	const revenueEditLog = dashboard?.revenue_edit_log || [];
	const costEditLog = dashboard?.cost_edit_log || [];

	const renderRevenueModel = () => {
		if (!revenueModel) {
			return (
				<EmptyState>
					<div className="empty-body">No revenue model configured.</div>
				</EmptyState>
			);
		}

		if (revenueModel.model_type === MODEL_COST_PLUS) {
			return (
				<Box>
					<PanelSubtitle style={{ marginBottom: 6 }}>Margin</PanelSubtitle>
					<div style={{ fontSize: 24, fontWeight: 600, color: '#18405d' }}>
						{revenueModel.cost_plus_margin_pct ?? '—'}%
					</div>
					<PanelSubtitle style={{ marginTop: 8 }}>
						Applied on top of total monthly cost to compute invoiceable revenue.
					</PanelSubtitle>
				</Box>
			);
		}

		return (
			<AntdTable
				rowKey={(record, index) => `slab-${index}`}
				size="small"
				columns={slabColumns}
				dataSource={revenueModel.slabs || []}
				pagination={false}
				scroll={{ x: 420 }}
			/>
		);
	};

	return (
		<>
			<Row gutter={[24, 24]}>
				<Col xs={24} xl={12}>
					<Panel>
						<PanelHeader>
							<Box>
								<PanelTitle>Revenue Model</PanelTitle>
								<PanelSubtitle>Defined at project creation</PanelSubtitle>
							</Box>
							<Button
								type="link"
								style={{ padding: 0 }}
								onClick={() => navigate(`/projects/${projectId}`)}
							>
								Edit in Project Setup
							</Button>
						</PanelHeader>

						{renderRevenueModel()}

						<Box mt="20px">
							<PanelHeader style={{ marginBottom: 8 }}>
								<Box>
									<PanelTitle style={{ fontSize: 13 }}>
										Additional Charges
									</PanelTitle>
									<PanelSubtitle>
										Metered charges layered on top of base revenue
									</PanelSubtitle>
								</Box>
								<Box style={{ fontSize: 13, color: '#18405d', fontWeight: 600 }}>
									MTD: {money(additionalChargesTotal)}
								</Box>
							</PanelHeader>

							{additionalCharges.length === 0 ? (
								<PanelSubtitle>No additional charges configured.</PanelSubtitle>
							) : (
								<AntdTable
									rowKey={(record, index) => record.id ?? `charge-${index}`}
									size="small"
									columns={chargeColumns}
									dataSource={additionalCharges}
									pagination={false}
									scroll={{ x: 380 }}
								/>
							)}
						</Box>
					</Panel>
				</Col>

				<Col xs={24} xl={12}>
					<FixedCostsPanel
						projectId={projectId}
						fixedCosts={fixedCosts}
						loading={loading}
						// Fixed Costs editing is temporarily disabled — panel is read-only
						canEdit={false}
						canDelete={false}
						costFrozen={Boolean(dashboard?.cost_frozen)}
						isCostPlus={revenueModel?.model_type === MODEL_COST_PLUS}
						onSaved={onDataChanged}
					/>
				</Col>
			</Row>

			<Box mt="16px">
				<InfoBanner tone="neutral">
					Fixed costs count in full towards MTD cost, not spread over the days of the
					month, and edits are versioned — previously reported months are not affected.
				</InfoBanner>
			</Box>

			<Row gutter={[24, 24]} style={{ marginTop: 8 }}>
				<Col xs={24} xl={12}>
					<Panel>
						<PanelHeader>
							<Box>
								<PanelTitle>Revenue Edit Log</PanelTitle>
								<PanelSubtitle>
									Changes to slabs / margin, made in Project Setup
								</PanelSubtitle>
							</Box>
							<PanelSubtitle>{revenueEditLog.length} entries</PanelSubtitle>
						</PanelHeader>

						{revenueEditLog.length === 0 ? (
							<EmptyState>
								<div className="empty-body">
									No revenue model changes recorded yet.
								</div>
							</EmptyState>
						) : (
							<AntdTable
								rowKey={(record, index) => `revenue-log-${index}`}
								size="small"
								columns={editLogColumns}
								dataSource={revenueEditLog}
								pagination={false}
								scroll={{ x: 640 }}
							/>
						)}
					</Panel>
				</Col>

				<Col xs={24} xl={12}>
					<Panel>
						<PanelHeader>
							<Box>
								<PanelTitle>Cost Edit Log</PanelTitle>
								<PanelSubtitle>
									Changes to fixed cost heads on this page
								</PanelSubtitle>
							</Box>
							<PanelSubtitle>{costEditLog.length} entries</PanelSubtitle>
						</PanelHeader>

						{costEditLog.length === 0 ? (
							<EmptyState>
								<div className="empty-body">
									No fixed cost changes recorded yet.
								</div>
							</EmptyState>
						) : (
							<AntdTable
								rowKey={(record, index) => `cost-log-${index}`}
								size="small"
								columns={editLogColumns}
								dataSource={costEditLog}
								pagination={false}
								scroll={{ x: 640 }}
							/>
						)}
					</Panel>
				</Col>
			</Row>
		</>
	);
}

FixedTab.propTypes = {
	projectId: PropTypes.number.isRequired,
	revenueModel: PropTypes.object,
	fixedCosts: PropTypes.array,
	dashboard: PropTypes.object,
	loading: PropTypes.bool,
	onDataChanged: PropTypes.func.isRequired,
};

FixedTab.defaultProps = {
	revenueModel: null,
	fixedCosts: [],
	dashboard: null,
	loading: false,
};
